<template>
<v-container fluid>
	<v-card>
		<v-card-title class="pb-0" v-if="false">
			<v-switch
			label="Show filtered data"
			v-model="viewFiltered"></v-switch>
			<v-btn
			flat
			:icon="$vuetify.breakpoint.xsOnly"
			@click="$root.$emit('dialogue', {scripting: true})"
			>
			<v-icon :left="$vuetify.breakpoint.smAndUp" small>fa-filter</v-icon>
			<span class="hidden-xs-only">Scripting …</span>
			</v-btn>
		</v-card-title>
		<v-container fluid>
		
			<v-layout row wrap>
			
			<v-flex xs12 sm4 order-xs2 order-sm1>
			<v-switch
			label="Enable selection"
			v-model="selectEnabled"></v-switch>
			</v-flex>
			
			<v-flex xs12 sm6 offset-sm2 order-xs1 order-sm2>
			<v-text-field
			v-model="search"
			append-outer-icon="search"
			:append-icon="search ? 'clear' : ''"
			@click:append="() => search = null"
			:hint="'Enter some text or an NRPL expression'"
			:success-messages="search && searchStatusMessages || ''"
			:error-messages="searchErrorMessages"
			></v-text-field>
			</v-flex>
			
			</v-layout>
			
		</v-container>
		<v-data-table
		:headers="headers"
		:items="asyncFilteredItems || displayItems"
		item-key="_id"
		:pagination.sync="tableDefaults"
		:loading="isLoading"
		v-model="selection"
		:select-all="selectEnabled"
		>
			<template slot="items" slot-scope="props">
				<tr
				@click="selectEnabled && (props.selected = !props.selected)">
					<td v-if="selectEnabled"><v-checkbox v-model="props.selected" readonly hide-details/>
					<td>{{props.item.fix_id}}</td>
					<td>
						<v-chip outline small style="width: 100%"
						@click="props.expanded = !props.expanded">
							<v-avatar v-if="props.item.token_avatar">
								<img :src="props.item.token_avatar"/>
							</v-avatar>
							{{ props.item.token_name }}
						</v-chip>
					</td>
					<td>{{ props.item.tstamp }}</td>
					<td>{{ props.item.latitude }}</td>
					<td>{{ props.item.longitude }}</td>
					<td>{{ props.item.altitude }}</td>
					<td>{{ props.item.speed }}</td>
					<td>{{ props.item.direction }}</td>
					<td>{{ props.item.created }}</td>
				</tr>
			</template>
			
			<template slot="expand" slot-scope="props">
				<v-card flat>
					<v-card-text>
						<template v-for="(value, key) in props.item.metadata">
							<v-chip color="secondary lighten-1" outline label small class="mr-0">{{key}}</v-chip>
							<v-chip color="primary" outline label small class="ml-0">{{value || "␀"}}</v-chip>
						</template>
					</v-card-text>
				</v-card>
			</template>
			
			<v-progress-linear slot="progress" :value="progress"></v-progress-linear>
			
		</v-data-table>
	</v-card>
</v-container>
</template>

<script>
import * as moment from 'moment'
import Vuex from 'vuex'
import api from '../assets/js/api-instance'
import NRPL from 'nrpl'

export default {
	name: "FollowMap",
	
	data () {
		return {
			headers: [
				{ text: "ID", value: "fix_id" },
				{ text: "Token", value: "token_id" },
				{ text: "Timestamp", value: "tstamp" },
				{ text: "Latitude", value: "latitude" },
				{ text: "Longitude", value: "longitude" },
				{ text: "Altitude", value: "altitude" },
				{ text: "Speed", value: "speed" },
				{ text: "Direction", value: "direction" },
				{ text: "Received on", value: "created" },
			],
			tableDefaults: {
				sortBy: "fix_id",
				descending: true
			},
			isLoading: false,
			progress: 0,
			viewFiltered: false,
			search: "",
			searchErrorMessages: null,
			nrplMessages: null,
			lastSearch: "",
			asyncFilteredItems: null,
			selectEnabled: false,
			selection: []
		};
	},

	watch: {
		"user.tokens": {
			handler () {
				// Remove non-public layers on logout
				console.log("USER WATCH", this.user);
				if (!this.user.user_id) {
					console.log("MAP: LOGOUT EVENT");
					// TODO Public layers not implemented yet. Remove all.
					this.map.eachLayer(l => l instanceof L.FeatureGroup && l.remove());
				}
			}
		},
		
		async searchStatus (status) {
			this.searchErrorMessages = null;
			this.isLoading = false; // If an NRPL search is in progress, this will cancel it.
			this.viewFiltered = false; // If there was a previous search, nix it.
			let searchResults;
			
			if (status.textSearch) {
				// This applies the text search to the view, and then uses
				// the results from the view to modify the model via setting
				// $store.state.tokenData.filtered which in turn is picked up
				// by this.fixes when we set this.viewFiltered.
				// This will cause the view to be recomputed.
				searchResults = this.displayItems.filter(item => 
					Object.values(item)
					.some(value => this.filterText(value, status.textSearch))
				)
				.map( item => this.fixes[item._id] );
			} else if (status.nrplSearch) {
				searchResults = await this.filterNRPL(this.fixes, status.nrplSearch);
				// The NRPL filter might have errored or been cancelled mid-run,
				// we set this.viewFiltered accordingly
			}
			this.$store.commit('setFiltered', searchResults);
			this.viewFiltered = !!searchResults;
		}
	},
	
	computed: {
		...Vuex.mapState(["user", "tokenData"]),
		
		visibleLayers () {
			return Object.keys(this.tokenData.tokenState)
			.filter(token_id => this.tokenData.tokenState[token_id].visible);
		},
		
		fixes () {
			let count = 0;
			const dataset = this.viewFiltered
				? Object.values(this.$store.state.tokenData.filtered)
				: Object.values(this.$store.state.tokenData.fixes)
				
			return dataset
			.reduce( (a, b) => [...a, ...b], [])
			.filter(fix => this.visibleLayers.includes(fix.token_id));
		},
		
		displayItems () {
			let count = 0;
			return this.fixes.map( fix => {
				return Object.assign(
					{},
					fix,
					{
						_id: count++,
						token_avatar: this.tokenNames[fix.token_id].avatar,
						token_name: this.tokenNames[fix.token_id].name,
						tstamp: this.$options.filters.time(fix.tstamp),
						latitude: this.$options.filters.angle(fix.latitude, 2),
						longitude: this.$options.filters.angle(fix.longitude, 3),
						altitude: this.$options.filters.number(fix.altitude, 1),
						speed: this.$options.filters.number(fix.speed * 3.6, 0),
						direction: this.$options.filters.angle(fix.direction),
						created: this.$options.filters.time(fix.created)
					}
				);
			});
		},
		
		tokenNames () {
			let names = {};
			for (const token of this.tokenData.tokens) {
				const name = (token.metadata.name || token.token_id);
				const shared = !!token.shared_by;
				const avatar = token.metadata.imageUrl;
				names[token.token_id] = { name, shared, avatar };
			}
			return names;
		},
		
		searchStatus () {
			const status = {};
			const search = this.search && this.search.trim();
			if (search) {
				if (/^(<<|«)/.test(search)) {
					// NRPL expression
					status.nrpl = true
					if (/(>>|»)$/.test(search)) {
						// fully typed in
						status.nrplSearch =
							this.search.replace(/^<</, "«").replace(/>>$/, "»")+" eval";
					}
				} else {
					// Text expression
					status.textSearch = search.toLowerCase();
				}
			} else {
				// Empty expression
			}
			return status;
		},
		
		searchStatusMessages () {
			let messages;
			if (this.searchStatus.textSearch) {
				messages = "Plain text search results";
			} else if (this.searchStatus.nrplSearch) {
				if (this.nrplMessages) {
					messages = this.nrplMessages;
				} else {
					if (this.isLoading) {
						messages = "Running NRPL search";
					} else {
						messages = "NRPL search results";
					}
				}
			} else if (this.searchStatus.nrpl) {
				messages = "NRPL search (waiting for closing ‘»’)";
			}
			
			return messages;
		}
	},
	
	methods: {
		async exec () {
			const nrpl = new NRPL(null, null, true);
			
			const result = await nrpl.exec(this.script, [...this.fixes]);
			this.stack = JSON.stringify(result, null, 4);
		},
		
		save () {
			this.filtered = JSON.parse(this.stack)
				.filter(item => item instanceof Object && item.token_id);
			this.$store.commit('setFiltered', this.filtered);
		},
		
		filterText (val, search) {
			return val != null && typeof val !== 'boolean' &&
				val.toString().toLowerCase().indexOf(search) !== -1;
		},
		
		async filterNRPL (items, script) {
			//console.log("NRPL", script);
			
			let currentItem = {};
			const getVar = (name) => {
				return currentItem[name];
			};
			
			const nrpl = new NRPL(getVar, null, true);
			let resultItems = [];
			let processingMessages;
			
			const total = items.length;
			let count = 0;
			const timer = setInterval( () => {
				this.progress = (count++) / total * 100;
			}, 250);
			this.isLoading = true;
			
			for (const item of items) {
				if (this.isLoading) {
					currentItem = item;
					try {
						const result = await nrpl.exec(script);
						count++;
						if (!result) {
							console.warn("NRPL Error?");
						} else if (result[result.length-1]) {
							resultItems.push(item);
							if (result.length > 1) {
								processingMessages =
									"<span class='warning--text'>There are multiple items on the stack. Is your NRPL expression correct?</span>";
							}
						}
					} catch (err) {
						console.warn("NRPL error", err);
						this.searchErrorMessages = "Invalid NRPL expression";
						break;
					}
				} else {
					// Someone has edited the filter, so we
					// need to cancel this operation
					resultItems = null
					break;
				}
			}
			
			this.nrplMessages = processingMessages;
			clearInterval(timer);
			this.isLoading = false;
			this.progress = 0;
			return resultItems;
		}
	},
	
	filters: {
		time(value) {
			return moment(value).toISOString();
		},
		
		angle(value, pad = 0) {
			const i = Number.parseInt(value);
			const f = value - i;
			const d = String(i).padStart(pad, '0');
			const m = String(Number.parseInt(f * 60)).padStart(2, '0');
			const s = (f*3600%60).toFixed(1).padStart(4, '0');
			return isNaN(i) ? value : `${d}°${m}'${s}"`;
		},
		
		number(value, digits = -1, padding = 0, fill = '0') {
			if (digits < 0) {
				return String(value).padStart(padding, fill);
			} else {
				return Number(value).toFixed(digits).padStart(padding, fill);
			}
		}
	}
}
</script>
